import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx-js-style';

import type { AssessmentStudentRow } from '../types/AssessmentTable.type';
import {
  assessmentMonthColors,
  levelAlternativeChipColors,
  levelChipColors,
} from './colorHandler';

const XLSX_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const exportRowsToXlsx = (
  rows: Array<Record<string, unknown>>,
  fileName: string,
  sheetName: string,
) => {
  const headers = rows.length > 0 ? Object.keys(rows[0]) : [];
  const worksheet = XLSX.utils.json_to_sheet(rows, { header: headers });
  const workbook = XLSX.utils.book_new();

  worksheet['!cols'] = headers.map(() => ({ width: 20 }));

  const headerStyle = {
    font: {
      bold: true,
      color: { rgb: 'FFFFFF' },
    },
    fill: {
      patternType: 'solid',
      fgColor: { rgb: '4DA7DB' },
    },
    alignment: {
      vertical: 'center',
      horizontal: 'center',
    },
  };

  headers.forEach((_, columnIndex) => {
    const address = XLSX.utils.encode_cell({ r: 0, c: columnIndex });
    if (worksheet[address]) {
      worksheet[address].s = headerStyle;
    }
  });

  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.slice(0, 31));

  const workbookBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
    cellStyles: true,
  });

  saveAs(new Blob([workbookBuffer], { type: XLSX_MIME_TYPE }), fileName);
};

const assessmentMonths = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
];

const assessmentMonthLegendColors: Record<string, string> = {
  JAN: '#f4cccc',
  FEB: '#ea9999',
  MAR: '#d9d2e9',
  APR: '#f9cb9c',
  MAY: '#ffe599',
  JUN: '#d9d9d9',
  JUL: '#b6d7a8',
  AUG: '#a9d18e',
  SEP: '#9dc3e6',
  OCT: '#6fa8dc',
  NOV: '#38761d',
  DEC: '#d5a6bd',
};

const assessmentLevelPalette = {
  ...levelChipColors,
  ...levelAlternativeChipColors,
};

const colorWithoutHash = (color: string) =>
  color.replace('#', '').toUpperCase();

const assessmentMonthFor = (value: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Bangkok',
    month: 'short',
  })
    .format(date)
    .toUpperCase();
};

const assessmentOtherInfo = (row: AssessmentStudentRow) =>
  [
    `Parent: ${row.otherInfo.parentName || '—'}`,
    `Phone: ${row.otherInfo.phone || '—'}`,
    `Email: ${row.otherInfo.email || '—'}`,
  ].join('\n');

export const exportAssessmentProgressToXlsx = (
  rows: AssessmentStudentRow[],
  program: AssessmentStudentRow['program'],
  fileName: string,
) => {
  const title = `${program === 'SOF' ? 'SCHOOL OF FISH' : 'FLYING FISH'} - PROGRESS`;
  const worksheet = XLSX.utils.aoa_to_sheet([
    [title],
    ['Month Colour', ...assessmentMonths],
    [],
    ['Student ID', 'Name', 'Age', 'Level', 'Other Info', 'Last Update'],
    ...rows.map((row) => [
      row.studentId,
      row.fullName,
      row.age,
      row.level?.trim().toLowerCase() === 'none' ? '' : row.level || '',
      assessmentOtherInfo(row),
      assessmentMonthFor(row.lastLevelUpdatedAt),
    ]),
  ]);

  const titleStyle = {
    font: { bold: true, sz: 16, color: { rgb: '1F2937' } },
    alignment: { horizontal: 'center', vertical: 'center' },
  };
  const headerStyle = {
    font: { bold: true, color: { rgb: '1F2937' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'D9EAD3' } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      bottom: { style: 'thin', color: { rgb: '808080' } },
    },
  };
  const monthLabelStyle = {
    font: { bold: true, color: { rgb: '1F2937' } },
    alignment: { horizontal: 'center', vertical: 'center' },
  };

  worksheet['A1'].s = titleStyle;
  worksheet['A2'].s = {
    ...monthLabelStyle,
    fill: { patternType: 'solid', fgColor: { rgb: 'F3F4F6' } },
  };
  worksheet['A4'].s = headerStyle;

  assessmentMonths.forEach((month, index) => {
    const address = XLSX.utils.encode_cell({ r: 1, c: index + 1 });
    worksheet[address].s = {
      ...monthLabelStyle,
      fill: {
        patternType: 'solid',
        fgColor: { rgb: colorWithoutHash(assessmentMonthLegendColors[month]) },
      },
    };
  });

  const dataStartRow = 4;
  const dataEndRow = dataStartRow + rows.length - 1;
  ['A', 'B', 'C', 'D', 'E', 'F'].forEach((column) => {
    worksheet[`${column}4`].s = headerStyle;
  });

  rows.forEach((row, index) => {
    const rowNumber = dataStartRow + index;
    const level = row.level?.trim() || 'None';
    const levelColor = assessmentLevelPalette[level] || '#5B6472';
    const levelBackground = assessmentLevelPalette[`${level}_BG`] || '#EEF0F3';
    const month = assessmentMonthFor(row.lastLevelUpdatedAt);
    const monthColor = assessmentMonthColors[month] || '#6B7280';

    worksheet[`D${rowNumber + 1}`].s = {
      font: { color: { rgb: colorWithoutHash(levelColor) } },
      fill: {
        patternType: 'solid',
        fgColor: { rgb: colorWithoutHash(levelBackground) },
      },
      alignment: { horizontal: 'center', vertical: 'center' },
    };
    worksheet[`E${rowNumber + 1}`].s = {
      alignment: { vertical: 'top', wrapText: true },
    };
    worksheet[`F${rowNumber + 1}`].s = {
      font: { bold: true, color: { rgb: colorWithoutHash('#FFFFFF') } },
      fill: {
        patternType: 'solid',
        fgColor: { rgb: colorWithoutHash(monthColor) },
      },
      alignment: { horizontal: 'center', vertical: 'center' },
    };
  });

  worksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }];
  worksheet['!cols'] = [
    { wch: 14 },
    { wch: 28 },
    { wch: 8 },
    { wch: 18 },
    { wch: 42 },
    { wch: 16 },
    ...assessmentMonths.map(() => ({ wch: 10 })),
  ];
  worksheet['!rows'] = [
    { hpt: 28 },
    { hpt: 22 },
    { hpt: 8 },
    { hpt: 24 },
    ...rows.map(() => ({ hpt: 48 })),
  ];
  if (rows.length > 0) {
    worksheet['!autofilter'] = { ref: `A4:F${dataEndRow + 1}` };
  }

  const workbook = XLSX.utils.book_new();
  const sheetName =
    program === 'SOF' ? 'SCHOOL OF FISH - INPUT' : 'FLYING FISH - INPUT';
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  const workbookBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
    cellStyles: true,
  });

  saveAs(new Blob([workbookBuffer], { type: XLSX_MIME_TYPE }), fileName);
};
